import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/common";

function ClaimForm({ onAdded }: { onAdded: (p: any) => void }) {
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try { onAdded((await api.post("/account/pieces/claim", { serial })).data); setSerial(""); toast("Piece added to your account"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-neutral-200 bg-white p-6 md:p-8 max-w-xl" data-testid="pieces-claim-form">
      <p className="font-display text-2xl">Register a piece</p>
      <p className="text-sm text-neutral-600 mt-2">Enter the serial number from your certificate (for example SOM-2026-12345). It must be registered to the email on this account.</p>
      <div className="mt-5 flex gap-3 items-end">
        <div className="flex-1"><Field label="Serial number"><input required value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} placeholder="SOM-2026-12345" className={inputCls} data-testid="pieces-claim-serial" /></Field></div>
        <button disabled={busy} className="bg-neutral-900 text-white px-6 py-3 eyebrow text-[11px] hover:bg-[var(--c-primary)] transition-colors disabled:opacity-50" data-testid="pieces-claim-submit">{busy ? "Checking…" : "Add"}</button>
      </div>
    </form>
  );
}

function PieceCard({ p, onToggle }: any) {
  return (
    <div className="bg-white border border-neutral-200 grid grid-cols-[110px_1fr] md:grid-cols-[150px_1fr] gap-5 p-4" data-testid={`piece-card-${p.serial}`}>
      <div className="aspect-[4/5] bg-neutral-100 overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />}</div>
      <div className="min-w-0">
        <p className="eyebrow text-[10px] text-[var(--c-primary)] flex items-center gap-1.5"><ShieldCheck size={12} />Certificate of Provenance</p>
        <p className="font-display text-2xl mt-1">{p.title}</p>
        <p className="text-xs tracking-[0.2em] mt-1 text-neutral-500" data-testid={`piece-serial-${p.serial}`}>{p.serial}</p>
        <p className="text-sm mt-3 text-neutral-700">{[p.metal, p.stones?.[0] && [p.stones[0].carat && `${p.stones[0].carat} ct`, p.stones[0].shape, p.stones[0].stone].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</p>
        {p.igi_number && <p className="text-xs text-neutral-500 mt-1">IGI {p.igi_number}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link to={`/provenance/${p.token}`} className="eyebrow text-[10px] link-u" data-testid={`piece-view-${p.serial}`}>View certificate</Link>
          {p.has_making_of && <Link to={`/provenance/${p.token}/making-of`} className="eyebrow text-[10px] link-u" data-testid={`piece-making-of-${p.serial}`}>Watch it being made</Link>}
          <label className="flex items-center gap-2 text-xs text-neutral-600"><input type="checkbox" checked={p.reminders} onChange={(e) => onToggle(p, e.target.checked)} data-testid={`piece-reminders-${p.serial}`} />Anniversary & care reminders</label>
        </div>
        {p.has_making_of && <UpdatePrefs p={p} />}
      </div>
    </div>
  );
}

function UpdatePrefs({ p }: any) {
  const [ch, setCh] = useState(!p.owner_phone && (p.notify_channels || "both") !== "email" ? "email" : p.notify_channels || "both");
  const [phone, setPhone] = useState(p.owner_phone || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/account/pieces/${p.serial}/updates`, { channels: ch, phone }); toast("Update preferences saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 border-t border-neutral-100 pt-4 space-y-3" data-testid={`piece-updates-${p.serial}`}>
      <p className="eyebrow text-[10px] text-neutral-500">Making-of updates by</p>
      <div className="flex flex-wrap gap-2">{([["email", "Email"], ["sms", "Text"], ["both", "Both"]] as const).map(([k, l]) => (
        <button key={k} type="button" onClick={() => setCh(k)} className={`px-4 py-1.5 text-xs border transition-colors ${ch === k ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`} data-testid={`piece-updates-${k}-${p.serial}`}>{l}</button>))}</div>
      {ch !== "email" && <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile, e.g. 0412 345 678" className={`${inputCls} max-w-xs`} data-testid={`piece-updates-phone-${p.serial}`} />}
      <button type="button" disabled={busy} onClick={save} className="block eyebrow text-[10px] link-u disabled:opacity-50" data-testid={`piece-updates-save-${p.serial}`}>{busy ? "Saving…" : "Save preferences"}</button>
    </div>
  );
}

export function MyPieces() {
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => { api.get("/account/pieces").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const toggle = async (p: any, on: boolean) => {
    try { await api.put(`/account/pieces/${p.serial}/reminders`, { on }); setItems((l) => (l || []).map((x) => (x.serial === p.serial ? { ...x, reminders: on } : x))); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items) return null;
  return (
    <div className="space-y-8" data-testid="my-pieces">
      {items.length > 0 ? <div className="grid lg:grid-cols-2 gap-5">{items.map((p) => <PieceCard key={p.serial} p={p} onToggle={toggle} />)}</div>
        : <p className="text-sm text-neutral-600" data-testid="my-pieces-empty">No pieces registered yet. Add your bespoke piece below to keep its certificate, story and care reminders in one place.</p>}
      <ClaimForm onAdded={(p) => setItems((l) => [...(l || []).filter((x) => x.serial !== p.serial), p])} />
    </div>
  );
}
